import { normalizeWidgetTheme, APIURL, cancelSelectEvent, getCSSStyles, getEChartsTooltipTheme, getWidgetDataURI, apiGet, toggleWidgetTheme, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin } from '../../helpers';
import { BetaWidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';

interface SectorChartData {
  sectors: string[];
  avg_betas: number[];
  companies: number[];
}

interface IndustryChartData {
  industries: string[];
  avg_betas: number[];
  companies: number[];
}

interface SectorBetaApiData {
  parameters: {
    sector: string;
  };
  overall_statistics: {
    total_sectors: number;
    total_industries: number;
    total_companies: number;
    overall_avg_beta: number;
    overall_min_beta: number;
    overall_max_beta: number;
    overall_stddev_beta: number;
  };
  sector_data: Array<{
    sector: string;
    avg_beta: number;
    companies: number;
    min_beta: number;
    max_beta: number;
    stddev_beta: number;
  }>;
  selected_sector_industries: Array<{
    industry: string;
    avg_beta: number;
    companies: number;
    min_beta: number;
    max_beta: number;
  }>;
  chart_data: {
    sector_chart: SectorChartData;
    industry_chart: IndustryChartData;
  };
}


const BETA_COLORS: string[] = [
  '#DC2626', '#E44D26', '#EF6C00', '#F9A825',
  '#C0CA33', '#7CB342', '#43A047', '#2E7D32', '#1B5E20'
];

export class TraderverseBeta {
  private container: HTMLElement;
  private config: BetaWidgetConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private shadowRoot: ShadowRoot;
  private apiData: SectorBetaApiData | null = null;
  private currentChart: any;
  private widgetResizeObserver: ResizeObserver | null = null;
  private filters = {
    view: 'sectors' as 'sectors' | 'industries',
    sector: 'Technology'
  };

  constructor(config: BetaWidgetConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);

    if (this.config.sector) {
      this.filters.sector = this.config.sector;
    }
    if (this.config.view === 'sectors' || this.config.view === 'industries') {
      this.filters.view = this.config.view as 'sectors' | 'industries';
    }

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }
    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.updateDisplay();
    });
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }

  private getDefaultConfig(): Partial<BetaWidgetConfig> {
    return {
      headerPinned: true,
      theme: 'default-dark',
      width: '100%',
      height: '400px',
      autosize: true,
      sector: 'Technology',
      view: 'sectors'
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    this.createWidget();
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
    await this.injectChartLibrary();
    await this.attachStyles();
    this.setupEventListeners();
    await this.fetchData();
    if (widget) widget.classList.remove('loading');
    this.emit('ready', {});
    cancelSelectEvent(this.shadowRoot);
    this.config.onReady?.();
  }

  private createWidget(): void {
    let themeClass = 'defaultDarkMode';
    let dataTheme = 'default-dark';

    if (this.config.theme === 'white') {
      themeClass = 'whiteMode';
      dataTheme = 'white';
    } else if (this.config.theme === 'black') {
      themeClass = 'blackMode';
      dataTheme = 'black';
    } else if (this.config.theme === 'default-lite') {
      themeClass = 'defaultLightMode';
      dataTheme = 'default-lite';
    }

    const widgetHtml = `
      <div style="width: ${typeof this.config.width == 'number' ? this.config.width + 'px' : this.config.width};height: ${typeof this.config.height == 'number' ? this.config.height + 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
        <div class="headTop">
          <h4 class="title market">${this.getTitleIconSvg()} Beta</h4>
          <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
            <div class="item">
              <button class="filters-toggle" type="button"> <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/> </svg> </button>
            </div>
          </div>
        </div>
        <div class="inBody">
          <div class="optionsRow">
            <div class="item"></div>
            <span></span>
            <div class="filtersBox sideBar hidden">
              <div class="item">
                <label>View Type</label>
                <select class="filter-view-type">
                  <option value="sectors" ${this.filters.view === 'sectors' ? 'selected' : ''}>Sectors</option>
                  <option value="industries" ${this.filters.view === 'industries' ? 'selected' : ''}>Industries</option>
                </select>
              </div>
              <div class="item filter-sector-container" style="display: ${this.filters.view === 'industries' ? 'block' : 'none'};">
                <label>Sector</label>
                <select class="filter-sector">
                  <option value="Basic Materials" ${this.filters.sector === 'Basic Materials' ? 'selected' : ''}>Basic Materials</option>
                  <option value="Technology" ${this.filters.sector === 'Technology' ? 'selected' : ''}>Technology</option>
                  <option value="Real Estate" ${this.filters.sector === 'Real Estate' ? 'selected' : ''}>Real Estate</option>
                  <option value="Healthcare" ${this.filters.sector === 'Healthcare' ? 'selected' : ''}>Healthcare</option>
                  <option value="Financial Services" ${this.filters.sector === 'Financial Services' ? 'selected' : ''}>Financial Services</option>
                  <option value="Energy" ${this.filters.sector === 'Energy' ? 'selected' : ''}>Energy</option>
                  <option value="Consumer Cyclical" ${this.filters.sector === 'Consumer Cyclical' ? 'selected' : ''}>Consumer Cyclical</option>
                  <option value="Industrials" ${this.filters.sector === 'Industrials' ? 'selected' : ''}>Industrials</option>
                  <option value="Communication Services" ${this.filters.sector === 'Communication Services' ? 'selected' : ''}>Communication Services</option>
                  <option value="Consumer Defensive" ${this.filters.sector === 'Consumer Defensive' ? 'selected' : ''}>Consumer Defensive</option>
                  <option value="Utilities" ${this.filters.sector === 'Utilities' ? 'selected' : ''}>Utilities</option>
                </select>
              </div>
            </div>
          </div>
          <div class="chartDv" id="betaChartDv" style="height:320px;"></div>
        </div>
      </div>`;

    this.shadowRoot.innerHTML = widgetHtml;

    const filterBtn = this.shadowRoot.querySelector('.filters-toggle') as HTMLButtonElement;
    const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement;
    if (filterBtn && filtersBox) {
      filterBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        filtersBox.classList.toggle('hidden');
      });
    }
  }

  private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);

    const viewTypeSelect = this.shadowRoot.querySelector('.filter-view-type') as HTMLSelectElement | null;
    const sectorSelect = this.shadowRoot.querySelector('.filter-sector') as HTMLSelectElement | null;
    const sectorContainer = this.shadowRoot.querySelector('.filter-sector-container') as HTMLElement | null;

    if (viewTypeSelect) {
      viewTypeSelect.addEventListener('change', async (e) => {
        const val = (e.target as HTMLSelectElement).value as 'sectors' | 'industries';
        this.filters.view = val;
        
        if (sectorContainer) {
          sectorContainer.style.display = val === 'industries' ? 'block' : 'none';
        }
        
        if (this.apiData) {
          this.updateDisplay();
        } else {
          await this.fetchData();
        }
      });
    }

    if (sectorSelect) {
      sectorSelect.addEventListener('change', async (e) => {
        const val = (e.target as HTMLSelectElement).value;
        this.filters.sector = val;
        await this.fetchData();
      });
    }
  }

  private async injectChartLibrary(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if ((window as any).echarts) return resolve(true);
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
      script.async = true;
      script.onload = () => resolve(true);
      this.shadowRoot.appendChild(script);
    });
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

  private async fetchData(): Promise<void> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');

    try {
      const res = await apiGet({
        url: `${APIURL}/v1/equity-market/beta`,
        query: {
          sector: this.filters.sector
        }
      });

      if (!res.ok) {
        throw new Error('Failed to fetch beta data');
      }

      const json = await res.json();
      this.apiData = json.data;

      this.updateDisplay();
      this.emit('dataUpdate', this.apiData);
    } catch (err) {
      console.error('Failed to fetch beta data:', err);
      this.apiData = null;
      this.updateDisplay();
      this.emit('error', err);
    } finally {
      if (widget) widget.classList.remove('loading');
    }
  }

  private updateDisplay(): void {
    if (!(window as any).echarts) {
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }
    this.renderChart();
  }

  private getBetaColor(index: number, total: number): string {
    if (total <= 1) return BETA_COLORS[0];
    const ratio = index / (total - 1);
    const colorIndex = Math.min(Math.floor(ratio * (BETA_COLORS.length - 1)), BETA_COLORS.length - 1);
    return BETA_COLORS[colorIndex];
  }

  private renderChart(): void {
    const echarts = (window as any).echarts;
    const container = this.shadowRoot.querySelector('#betaChartDv') as HTMLElement;
    if (!container) return;

    if (this.currentChart) {
      this.currentChart.dispose();
      this.currentChart = null;
    }

    const isSectorsView = this.filters.view === 'sectors';
    let labels: string[] = [];
    let values: number[] = [];
    let companiesCount: number[] = [];

    if (isSectorsView) {
      const sectorChart = this.apiData?.chart_data?.sector_chart;
      labels = sectorChart?.sectors || [];
      values = sectorChart?.avg_betas || [];
      companiesCount = sectorChart?.companies || [];
    } else {
      const industryChart = this.apiData?.chart_data?.industry_chart;
      labels = industryChart?.industries || [];
      values = industryChart?.avg_betas || [];
      companiesCount = industryChart?.companies || [];
    }

    if (!labels.length) {
      container.innerHTML = '<div class="noData"><p>No beta data available</p></div>';
      return;
    }

    container.innerHTML = '';
    this.currentChart = echarts.init(container);

    const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    const textColor = getComputedStyle(widgetBox).getPropertyValue('--charts-text-color');
    const axisLineColor = getComputedStyle(widgetBox).getPropertyValue('--charts-axis-color');
    const splitLineColor = getComputedStyle(widgetBox).getPropertyValue('--charts-split-line-color');

    // Zip, sort descending by value, and unzip
    const zipped = labels.map((label, idx) => ({
      label,
      value: values[idx] ?? 0,
      companies: companiesCount[idx] ?? 0
    }));
    zipped.sort((a, b) => b.value - a.value);

    const chartLabels = zipped.map(item => item.label);
    const chartValues = zipped.map(item => item.value);

    const minBeta = Math.min(...chartValues);
    const maxBeta = Math.max(...chartValues);

    const option = {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        ...getEChartsTooltipTheme(widgetBox),
        formatter: (params: any) => {
          const point = Array.isArray(params) ? params[0] : params;
          const index = point?.dataIndex ?? 0;
          const item = zipped[index];
          if (!item) return '';
          return `
            <div>
              <strong>${item.label}</strong><br/>
              Beta: ${item.value.toFixed(2)}
            </div>
          `;
        }
      },
      grid: { left: 10, right: 10, top: 10, bottom: 60, containLabel: true },
      xAxis: {
        type: 'category',
        data: chartLabels,
        axisLine: { lineStyle: { color: axisLineColor } },
        axisTick: { show: false },
        axisLabel: {
          color: textColor,
          fontSize: 14,
          interval: 0,
          inside: true,
          rotate: 90,
        }
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 0.5,
        interval: 0.1,
        axisLine: { lineStyle: { color: axisLineColor } },
        axisTick: { show: false },
        splitLine: { show: true, lineStyle: { type: 'dotted', color: splitLineColor } },
        axisLabel: {
          color: textColor,
          fontSize: 14,
          formatter: (value: number) => value.toFixed(1)
        }
      },
      visualMap: {
        show: true,
        type: 'continuous',
        min: minBeta,
        max: maxBeta,
        orient: 'horizontal',
        left: 'center',
        bottom: 20,
        text: ['Avg Beta', ''],
        textStyle: { color: textColor, fontSize: 14 },
        itemWidth: 12,
        itemHeight: 300,
        inRange: {
          color: ['#DC2626', '#E44D26', '#EF6C00', '#F9A825', '#C0CA33', '#7CB342', '#43A047', '#2E7D32', '#1B5E20']
        },
        showLabel: false,
        calculable: false
      },
      series: [
        {
          name: 'Avg Beta',
          type: 'bar',
          data: chartValues,
          barWidth: '70%',
          zlevel: -1,
          itemStyle: {
            borderRadius: [4, 4, 0, 0]
          },
          label: { show: false }
        }
      ]
    };

    this.currentChart.setOption(option);
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }

  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
  }

  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }

  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite'): void {
    if (!this.shadowRoot) return;
    this.config.theme = toggleWidgetTheme(this.shadowRoot, theme) as 'white' | 'black' | 'default-dark' | 'default-lite';
    this.updateDisplay();
  }

  public destroy(): void {
    if (this.widgetResizeObserver) {
      this.widgetResizeObserver.disconnect();
      this.widgetResizeObserver = null;
    }
    if (this.currentChart) {
      this.currentChart.dispose();
      this.currentChart = null;
    }
    this.apiData = null;
    this.eventHandlers.clear();
    this.shadowRoot.innerHTML = '';
  }

  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) {
      const evt: WidgetEvent = { type, data };
      handlers.forEach(h => h(evt));
    }
  }

  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/>
            <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/>
          </svg>`;
  }
}

declare global {
  interface Window {
    TraderverseBeta: typeof TraderverseBeta;
  }
}
if (typeof window !== 'undefined') {
  window.TraderverseBeta = TraderverseBeta;
}
